import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAxios, useApiResponseParser, useAuth } from "../";

export interface ProjectDetails {
    Id?: string;
    OrganizationId?: string;
    Name: string;
    Description?: string;
    ProjectType?: string;
    EagleViewOrderId?: string;
    SalesmanName?: string;
    SecondarySalesmanName?: string;
    PrimarySalesmanId?: string;
    StartDate: string; // ISO string (e.g., from JSON), convert to Date when needed
    EstimatedEndDate?: string;
    ActualEndDate?: string;
    CustomerName?: string;
    CustomerPhoneNumber?: string;
    CustomerStreet?: string;
    CustomerCity?: string;
    CustomerState?: string;
    CustomerZipCode?: string;
    CustomerCountry?: string;
    InsuranceProvider?: string;
    InsuranceClaimNumber?: string;
    InsurancePolicyNumber?: string;
    InsuranceAgentNumber?: string;
    InsuranceAgentEmail?: string;
    Lead?: {
        Id: string;
        LeadSourcesId: string;
        Name?: string | null;
        LeadSource?: {
            Id: string;
            Name: string;
        } | null;
    } | null;
}

export default function useCreateProject() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();
    const { selectedOrganization } = useAuth();

    function useCreateProject() {
        return useMutation({
            mutationFn: (parameters: ProjectDetails) =>
                axiosPrivate
                    .post(`/v1/project/create`, parameters)
                    .then(parseSuccessResponseData),
        });
    }

    function useProjectAttributeUpdate() {
        return useMutation({
            mutationFn: (parameters: any) =>
                axiosPrivate
                    .put(`/v1/project/update`, parameters)
                    .then(parseSuccessResponseData),
        });
    }

    function useProjectUpdate() {
        return useMutation({
            mutationFn: (parameters: { Id: string } & any) =>
                axiosPrivate
                    .post(`/v1/project/update`, parameters)
                    .then(parseSuccessResponseData),
        });
    }

    function useSetProjectStatus() {
        return useMutation({
            mutationFn: (parameters: {
                Id: string;
                Status:
                    | "NOT-STARTED"
                    | "IN-PROGRESS"
                    | "COMPLETED"
                    | "CANCELLED";
            }) =>
                axiosPrivate
                    .post(`/v1/project/status`, parameters)
                    .then(parseSuccessResponseData),
        });
    }

    function useRevertProject() {
        return useMutation({
            mutationFn: (id: string) =>
                axiosPrivate
                    .delete(`/v1/project/revert/${id}`)
                    .then(parseSuccessResponseData),
        });
    }

    function useDeleteProject() {
        const queryClient = useQueryClient();
        return useMutation({
            mutationFn: (id: string) =>
                axiosPrivate
                    .delete(`/v1/project/delete/${id}`)
                    .then(parseSuccessResponseData),
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ["projects"] });
                queryClient.invalidateQueries({ queryKey: ["project-summary"] });
            },
        });
    }

    function useList() {
        const organizationId = selectedOrganization?.Id;
        const query = useQuery({
            queryKey: ["projects", organizationId ?? "none"],
            queryFn: () =>
                axiosPrivate
                    .get(`/v1/project/list`, {
                        params: organizationId
                            ? { OrganizationId: organizationId }
                            : undefined,
                    })
                    .then(parseSuccessResponseData),
            enabled: !!organizationId,
            staleTime: 30_000,
        });

        // Keep mutate/mutateAsync for existing refresh call sites; they share the same cache.
        return {
            ...query,
            isPending: query.isFetching,
            mutate: () => {
                void query.refetch();
            },
            mutateAsync: async () => {
                const result = await query.refetch();
                return result.data;
            },
        };
    }

    function useListById() {
        return useMutation({
            mutationFn: (params: { Id: string }) =>
                axiosPrivate
                    .get(`/v1/project/details`, { params })
                    .then(parseSuccessResponseData),
        });
    }

    function useGetProjectById(Id: string) {
        return useQuery({
            queryKey: ["project", Id],
            queryFn: () =>
                axiosPrivate
                    .get(`/v1/project/details`, { params: { Id } })
                    .then(parseSuccessResponseData),
            enabled: !!Id,
        });
    }

    function useGetEagleViewReport() {
        return useMutation({
            mutationFn: (params: { ReportId: string }) =>
                axiosPrivate
                    .get(`/v1/eagleview/load-report`, { params })
                    .then(parseSuccessResponseData),
        });
    }

    function useGetProjectStatusSummary() {
        return useQuery({
            queryKey: ["project-summary", selectedOrganization?.Id ?? "none"],
            queryFn: async () => {
                const response = await axiosPrivate.get(`/v1/project/status`, {
                    params: selectedOrganization?.Id
                        ? { OrganizationId: selectedOrganization.Id }
                        : undefined,
                });
                return parseSuccessResponseData(response);
            },
            enabled: !!selectedOrganization?.Id,
            staleTime: 30_000,
        });
    }

    function useGetEagleViewOrders() {
        return useMutation({
            mutationFn: () =>
                axiosPrivate
                    .get(`/v1/eagleview/orders`)
                    .then(parseSuccessResponseData),
        });
    }

    function usePlaceEagleViewOrder() {
        return useMutation({
            mutationFn: (parameters: {
                ProjectId: string;
                Address: {
                    Street: string;
                    City: string;
                    State: string;
                    Zip: string;
                    Country?: string;
                };
                ProductId: number;
                DeliveryProductId: number;
                MeasurementRequestTypeId: number;
                ClaimNumber?: string;
                PONumber?: string;
            }) =>
                axiosPrivate
                    .post(`/v1/eagleview/place-order`, parameters)
                    .then(parseSuccessResponseData),
        });
    }

    function useSyncEagleViewReport() {
        return useMutation({
            mutationFn: (parameters: { ProjectId: string }) =>
                axiosPrivate
                    .post(`/v1/eagleview/sync-report`, parameters)
                    .then(parseSuccessResponseData),
        });
    }

    return {
        useCreateProject,
        useList,
        useListById,
        useGetProjectById,
        useGetEagleViewReport,
        useGetEagleViewOrders,
        usePlaceEagleViewOrder,
        useSyncEagleViewReport,
        useGetProjectStatusSummary,
        useProjectAttributeUpdate,
        useProjectUpdate,
        useSetProjectStatus,
        useDeleteProject,
        useRevertProject,
    };
}
